import { resolveTikTokAdvertiserId } from '../utils/tiktokAdvertiserId';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faClock, faUpload, faStop, faExclamationTriangle, faDownload, faRocket, faExternalLinkAlt, faChartLine, faSpinner, faEdit, faRefresh, faLocationDot, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import {
  optimizeAnalysisTitles,
  ALL_ANALYSES_KIND,
  optimizeAnalysesEnabledFor,
} from '../utils/optimizeCampaignRun';
import StartOrchestrationModal from './StartOrchestrationModal';
import './CampaignCompletionStatus.css';

/**
 * The focused analyses as ONE button, REPLACING "Optimize Campaign" where they exist.
 *
 * "Optimize Campaign" runs the combined optimizer, which answers three questions in one
 * diluted response. This button kicks off all three focused analyses as SEPARATE runs —
 * diagnostic, pacing, performance — each with its own result in the activity feed, so
 * every answer arrives undiluted (the page handler expands the 'all' kind into the three
 * dispatches; see utils/optimizeCampaignRun.startAllFocusedAnalyses).
 *
 * DV360, Google Ads Search and Performance Max, Facebook and LinkedIn have the focused
 * launchers; every other platform keeps its "Optimize Campaign" button (each render site
 * gates that button on
 * !optimizeAnalysesEnabledFor). The tooltip is per-platform, because the three analyses
 * examine different objects on each. Gated on the same conditions as the Optimize button
 * at each call site.
 */
const FocusedAnalysisButtons = ({ destination, platformCampaignId, onOptimizeCampaign, isOptimizing, runningKind = null }) => {
  if (!onOptimizeCampaign || !optimizeAnalysesEnabledFor(destination)) return null;
  const busy = isOptimizing && runningKind === ALL_ANALYSES_KIND;
  return (
    <>
      <button
        onClick={() => onOptimizeCampaign(platformCampaignId, ALL_ANALYSES_KIND)}
        disabled={isOptimizing}
        title={optimizeAnalysisTitles(destination).join('\n')}
        className="btn btn-primary"
        style={{
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isOptimizing ? 'not-allowed' : 'pointer',
          opacity: isOptimizing && !busy ? 0.55 : 1
        }}
        onMouseEnter={(e) => { if (!isOptimizing) e.currentTarget.style.backgroundColor = '#0056b3'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#007bff'; }}
      >
        {busy
          ? <><FontAwesomeIcon icon={faSpinner} spin /> Running analyses...</>
          : <><FontAwesomeIcon icon={faChartLine} /> Run Full Analysis</>}
      </button>
    </>
  );
};

/**
 * The campaign quality audit, as one button beside Run Full Analysis.
 *
 * Its own component for the same reason FocusedAnalysisButtons is: the campaign
 * actions render in four different layout branches (TTD compact, multiple
 * campaigns, multiple urls, single), and markup duplicated four times is markup
 * that drifts.
 *
 * DV360 and Yahoo only — those are the platforms campaign_audit has an observer
 * for, and offering it elsewhere would produce a card that can only say it
 * cannot run. `launched` is passed rather than inferred because each branch
 * already knows, in its own terms, whether the campaign exists on the platform.
 */
const AUDITABLE_DESTINATIONS = ['dv360', 'yahoo'];

const RunAuditButton = ({ destination, platformCampaignId, onRunAudit, isAuditing, launched }) => {
  // HIDDEN FOR NOW. On-demand auditing is not offered yet: the button shipped
  // ungated and appeared on every account's launched DV360 and Yahoo
  // campaigns. Everything behind it is intact and unchanged — automatic
  // auditing still runs for accounts with `enable-campaign-audit`, and
  // POST /api/v2/campaigns/:id/audit still works — so showing it again is
  // deleting this one line.
  return null;

  // eslint-disable-next-line no-unreachable
  if (!onRunAudit || !platformCampaignId || !launched) return null;
  if (!AUDITABLE_DESTINATIONS.includes(String(destination || '').toLowerCase())) return null;
  return (
    <button
      onClick={onRunAudit}
      disabled={isAuditing}
      title="Compare the live campaign against the launch template it was built from"
      className="btn btn-outline"
      style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', border: '1px solid #6f42c1', color: '#6f42c1', borderRadius: '4px', backgroundColor: 'transparent', cursor: isAuditing ? 'not-allowed' : 'pointer' }}
      onMouseEnter={(e) => { if (!isAuditing) { e.currentTarget.style.backgroundColor = '#6f42c1'; e.currentTarget.style.color = 'white'; } }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6f42c1'; }}
    >
      {isAuditing
        ? <><FontAwesomeIcon icon={faSpinner} spin /> Starting audit…</>
        : <><FontAwesomeIcon icon={faClipboardCheck} /> Run audit</>}
    </button>
  );
};

/**
 * Statuses a campaign lands on once its launch has finished on the platform.
 *
 * Both mean "launched". `awaiting_creatives` is where a fast-track-SHELL
 * campaign lands: it is live on the DSP, it just has no creatives assigned
 * through our assign-creatives step yet — that step is what flips it to
 * `ready_to_launch` (routes/v2/orchestration.js).
 *
 * Optimization reads delivery data from the platform, which does not depend on
 * our assign-creatives step having run, and the backend's POST /:id/optimize
 * never gated on campaign status either. Checking `ready_to_launch` alone was
 * therefore an accidental creatives-assigned requirement: it hid Optimize from
 * every shell campaign on every platform until creatives were attached.
 */
const LAUNCHED_CAMPAIGN_STATUSES = new Set(['ready_to_launch', 'awaiting_creatives']);
export const hasLaunched = (status) => LAUNCHED_CAMPAIGN_STATUSES.has(String(status || ''));

/**
 * CampaignCompletionStatus component displays the campaign completion card
 * matching the mockup design with:
 * - Campaign name with checkmark
 * - Horizontal progress bar with 4 stages
 * - Sub-task checkmarks
 * - Completion indicator
 */
const CampaignCompletionStatus = ({
  campaign,
  orchestrationStatus,
  campaignMessages = [],
  isLoading = false,
  className = '',
  onStopOrchestration = null,
  onUploadCreatives = null,
  uploadCreativesData = null,
  accountId = null,
  userId = null,
  onCampaignUpdate = null, // Callback to refresh campaign data
  onOrchestrationStart = null, // Callback when orchestration starts
  onOptimizeCampaign = null, // Callback for optimize campaign — (platformCampaignId, analysisKind)
  optimizingAnalysisKind = null, // Which focused analysis is currently running, if any
  isOptimizing = false, // Whether optimization is in progress
  onRefreshCampaign = null, // Callback for refresh campaign
  onRunAudit = null, // Callback for the campaign quality audit
  isAuditing = false,
  isRefreshing = false, // Whether refresh is in progress
  platformUrls = null, // Platform URLs for view campaign buttons
  onEditCampaign = null, // Callback for edit campaign
  onCreateGeofenceAdGroups = null, // Callback for "Create Geofence Ad Groups" (TTD, feature-flagged)
  isCreatingGeofence = false // Whether a geofence ad group run is being dispatched
}) => {
  // State for start orchestration modal
  const [showStartOrchestrationModal, setShowStartOrchestrationModal] = React.useState(false);
  // State for YouTube DV360 actions dropdown
  const [showYoutubeActionsDropdown, setShowYoutubeActionsDropdown] = React.useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showYoutubeActionsDropdown && !event.target.closest('.youtube-actions-dropdown') && !event.target.closest('.sub-task-warning')) {
        setShowYoutubeActionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showYoutubeActionsDropdown]);

  // Handle SDF file downloads for YouTube DV360
  const handleDownloadSDFFiles = async (sdfFiles) => {
    if (!sdfFiles || !Array.isArray(sdfFiles)) {
      console.error('No SDF files available for download');
      return;
    }

    console.log('Downloading SDF files:', sdfFiles);

    for (let i = 0; i < sdfFiles.length; i++) {
      const file = sdfFiles[i];
      try {
        // Get signed URL from backend using the configured API instance
        const params = {};
        if (accountId) params.accountId = accountId;
        if (userId) params.userId = userId;

        // Add GCS path and filename as query parameters
        params.gcsPath = file.gcs_path;
        params.filename = file.filename;

        const response = await api.get(getApiUrl(`/api/v2/campaigns/${campaign.id}/sdf-files/download`), {
          params
        });

        if (response.data && response.data.publicUrl) {
          // Open download in new tab/window
          setTimeout(() => {
            window.open(response.data.publicUrl, '_blank');
          }, i * 500); // Stagger downloads by 500ms to avoid browser blocking
        } else {
          console.error('No download URL received for file:', file.filename);
        }
      } catch (error) {
        console.error('Error downloading SDF file:', file.filename, error);

        // Try to extract meaningful error message
        if (error.response) {
          const errorMessage = error.response.data?.error || `HTTP ${error.response.status}`;
          console.error('Failed to get download URL for file:', file.filename, errorMessage);
        }
      }
    }

    // Note: Campaign status remains 'awaiting_creatives' until assign-creatives completes
    // This matches the behavior of other platforms (DV360, TTD, etc.)
    console.log('SDF files download initiated. Campaign status will update after assign-creatives completes.');
  };
  // Handle loading state
  if (isLoading) {
    return (
      <div className={`campaign-completion-status loading ${className}`}>
        <div className="completion-header">
          <div className="campaign-title">
            <div className="loading-placeholder loading-icon"></div>
            <div className="loading-placeholder loading-title"></div>
          </div>
        </div>
        <div className="progress-stages">
          <div className="loading-placeholder loading-progress"></div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (!campaign) {
    return (
      <div className={`campaign-completion-status error ${className}`}>
        <div className="error-message">
          <span>Unable to load campaign information</span>
        </div>
      </div>
    );
  }

  // Check if this is an import-campaign scenario
  const isImportCampaign = campaignMessages.some(msg =>
    msg.action === 'import-campaign' || msg.message_type === 'import-campaign'
  );

  // Check if this is a single message scenario (no orchestration, but has single messages)
  // Include both processing and completed messages so the progress bar stays visible
  const singleMessages = campaignMessages.filter(msg =>
    ['upload-creatives'].includes(msg.message_type)
  );
  const isSingleMessageMode = !orchestrationStatus && singleMessages.length > 0;

  // Define the 4 main stages from the mockup
  const stages = [
    { key: 'processing', label: 'PROCESSING' },
    { key: 'configuring', label: 'CONFIGURING' },
    { key: 'calling_api', label: 'CALLING API' },
    { key: 'uploading', label: 'UPLOADING' }
  ];

  // Get workflow type and creative status (used throughout component)
  const creativeStatus = orchestrationStatus?.stepProgress?.creative?.status;
  const isShellWorkflow = creativeStatus === 'skipped';
  const isCopyWorkflow = orchestrationStatus?.copyWorkflow === true;

  // Get assign-creatives status from campaign messages (for fast-track-shell 4th bar)
  // Prioritize completed messages over others to ensure progress bar shows success
  const assignCreativesMessages = campaignMessages.filter(msg =>
    msg.action === 'assign-creatives' || msg.message_type === 'assign-creatives'
  );
  
  // Find the "best" status to display: completed > processing > failed/stopped
  const completedAssign = assignCreativesMessages.find(msg => msg.status === 'completed');
  const processingAssign = assignCreativesMessages.find(msg => msg.status === 'processing');
  const assignCreativesMessage = completedAssign || processingAssign || assignCreativesMessages[0];

  const assignCreativesStatus = assignCreativesMessage ? {
    status: assignCreativesMessage.status,
    progress: assignCreativesMessage.status === 'completed' ? 100 :
             assignCreativesMessage.status === 'failed' || assignCreativesMessage.status === 'timeout' ? 0 :
             assignCreativesMessage.status === 'processing' ? 50 : 0,
    estimatedTimeRemaining: assignCreativesMessage.status === 'completed' ? 0 :
                           assignCreativesMessage.status === 'failed' || assignCreativesMessage.status === 'timeout' ? 0 : 300
  } : null;

  // For shell workflows, check assign-creatives completion; for regular workflows, check creative completion
  const creativeCompleted = isCopyWorkflow && orchestrationStatus?.status === 'completed'
    ? true
    : isShellWorkflow
    ? assignCreativesStatus?.status === 'completed'
    : creativeStatus === 'completed';

  // Define sub-tasks with their column alignment
  // For shell workflows, move creative assets to the last column (UPLOADING/DOWNLOADING)

  const subTaskColumns = [
    {
      // Column 1 - Under PROCESSING
      tasks: isShellWorkflow
        ? [{ key: 'insertion_order', label: 'Insertion order' }]
        : [
            { key: 'insertion_order', label: 'Insertion order' },
            { key: 'creative_assets', label: 'Creative assets' }
          ]
    },
    {
      // Column 2 - Under CONFIGURING
      tasks: [
        { key: 'launch_template', label: 'Launch template' }
      ]
    },
    {
      // Column 3 - Under CALLING API
      tasks: []
    },
    {
      // Column 4 - Under UPLOADING
      tasks: isShellWorkflow
        ? [
            { key: 'creative_assets', label: 'Creative assets' }
          ]
        : [
            { key: 'uploaded_to_platform', label: `Uploaded to ${campaign?.destination?.toUpperCase() || 'Platform'}` }
          ]
    }
  ];

  // Determine stage completion based on v2 orchestration status with stepProgress
  const getStageStatus = (stageKey) => {
    if (!orchestrationStatus || !orchestrationStatus.stepProgress) return 'pending';

    // Global override: if orchestration is stopped, show appropriate status for each stage
    if (isStopped) {
      const currentStep = orchestrationStatus.currentStep;

      // Determine which stage was active when stopped
      let stoppedAtStage = 'processing'; // default

      if (currentStep === 'campaign') {
        stoppedAtStage = 'processing';
      } else if (currentStep === 'launcher') {
        stoppedAtStage = 'configuring';
      } else if (currentStep === 'api') {
        stoppedAtStage = 'calling_api';
      } else if (currentStep === 'assign-creatives' || currentStep === 'upload-creatives' || currentStep === 'creative') {
        stoppedAtStage = 'uploading';
      }

      // Show stopped status for the stage that was active when stopped
      if (stageKey === stoppedAtStage) {
        return 'stopped';
      }

      // For stages before the stopped stage, check if they were completed
      const stageOrder = ['processing', 'configuring', 'calling_api', 'uploading'];
      const stoppedStageIndex = stageOrder.indexOf(stoppedAtStage);
      const currentStageIndex = stageOrder.indexOf(stageKey);

      if (currentStageIndex < stoppedStageIndex) {
        // This stage was before the stopped stage, check if it was completed
        const stepMapping = {
          'processing': 'campaign',
          'configuring': 'launcher',
          'calling_api': 'api',
          'uploading': isShellWorkflow ? 'assign-creatives' : 'api'
        };

        const stepKey = stepMapping[stageKey];
        const stepData = orchestrationStatus.stepProgress[stepKey];

        return stepData?.status === 'completed' ? 'completed' : 'stopped';
      }

      // For stages after the stopped stage, show pending
      if (currentStageIndex > stoppedStageIndex) {
        return 'pending';
      }
    }

    // Handle uploading stage first - special case for shell workflows
    if (stageKey === 'uploading') {
      // Check upload progress from props
      const uploadInProgress = uploadCreativesData && uploadCreativesData.status === 'processing';
      const uploadCompleted = uploadCreativesData && uploadCreativesData.status === 'completed';

      // For shell workflows, use the assign-creatives status from campaign messages
      if (isShellWorkflow) {
        if (assignCreativesStatus) {
          if (assignCreativesStatus.status === 'failed' || assignCreativesStatus.status === 'timeout') return 'error';
          if (assignCreativesStatus.status === 'completed') return 'completed';
          if (assignCreativesStatus.status === 'processing') return 'processing';
          if (assignCreativesStatus.status === 'stopped') return 'stopped';
        }

        // Fallback to upload progress from props if no assign-creatives step data
        if (uploadCompleted || creativeCompleted) {
          return 'completed'; // Turn blue after creatives uploaded
        } else if (uploadInProgress) {
          return 'processing'; // Show as processing during upload
        } else {
          return 'pending'; // Keep gray until creatives uploaded
        }
      }

      // For regular workflows, use API status
      const apiData = orchestrationStatus.stepProgress.api;
      if (apiData) {
        if (apiData.status === 'failed') return 'error';

        // Check api_result.success for completed status
        if (apiData.status === 'completed') {
          // If we have api_result and success is false, show as error
          if (orchestrationStatus.apiResult && orchestrationStatus.apiResult.success === false) {
            return 'error';
          }
          return 'completed';
        }

        if (apiData.status === 'processing') return 'processing';
        if (apiData.status === 'stopped') return 'stopped';
      }

      return 'pending';
    }

    // Handle processing stage - normal logic (no special upload handling)
    if (stageKey === 'processing') {
      const campaignData = orchestrationStatus.stepProgress.campaign;
      if (!campaignData) return 'pending';

      // Normal processing stage logic - no upload animation since creative assets moved to UPLOADING
      if (campaignData.status === 'failed' || campaignData.status === 'timeout') return 'error';
      if (campaignData.status === 'completed') return 'completed';
      if (campaignData.status === 'processing') return 'processing';
      if (campaignData.status === 'stopped') return 'stopped';
      if (campaignData.status === 'skipped') return 'completed';

      return 'pending';
    }

    // Planning mode review: configuring is complete, calling_api shows review state
    if (orchestrationStatus.status === 'review') {
      if (stageKey === 'configuring') return 'completed';
      if (stageKey === 'calling_api') return 'review';
      if (stageKey === 'uploading') return 'pending';
    }

    // For other stages, use normal mapping
    const stepMapping = {
      'configuring': 'launcher',
      'calling_api': 'api'
    };

    const stepKey = stepMapping[stageKey];
    const stepData = orchestrationStatus.stepProgress[stepKey];

    if (stepData) {
      if (stepData.status === 'failed' || stepData.status === 'timeout') return 'error';

      if (stepData.status === 'completed') {
        // For API step, check api_result.success
        if (stepKey === 'api' && orchestrationStatus.apiResult && orchestrationStatus.apiResult.success === false) {
          return 'error';
        }
        return 'completed';
      }

      if (stepData.status === 'processing') return 'processing';
      if (stepData.status === 'stopped') return 'stopped';
      if (stepData.status === 'skipped') return 'completed'; // Treat skipped as completed for UI
    }

    return 'pending';
  };

  // Check orchestration status - for shell workflows, only consider completed if creatives are uploaded
  // (Variables already defined above)

  // For shell workflows, only consider truly completed when creatives are uploaded
  // Also check api_result.success for overall completion status
  const isCompleted = orchestrationStatus?.status === 'completed' &&
    (!isShellWorkflow || creativeCompleted) &&
    (orchestrationStatus?.apiResult?.success !== false); // Don't consider completed if API result failed
  const isStopped = orchestrationStatus?.status === 'stopped';
  const isFailed = orchestrationStatus?.status === 'failed' ||
    (orchestrationStatus?.apiResult?.success === false); // Consider failed if API result failed
  const isTimedOut = orchestrationStatus?.status === 'timeout' ||
    assignCreativesStatus?.status === 'timeout' ||
    orchestrationStatus?.stepProgress?.uploadCreatives?.status === 'timeout';

  // Determine sub-task completion based on v2 orchestration status with stepProgress
  const getSubTaskStatus = (taskKey) => {
    if (!orchestrationStatus || !orchestrationStatus.stepProgress) return 'pending';

    // Define shell workflow detection locally within this function
    const isShellWorkflowLocal = orchestrationStatus.stepProgress?.creative?.status === 'skipped';

    switch (taskKey) {
      case 'insertion_order':
        const campaignStep = orchestrationStatus.stepProgress.campaign;
        if (!campaignStep) return 'pending';
        return campaignStep.status === 'completed' ? 'completed' :
               campaignStep.status === 'processing' ? 'processing' :
               campaignStep.status === 'stopped' ? 'stopped' : 'pending';

      case 'launch_template':
        const launcherStep = orchestrationStatus.stepProgress.launcher;
        if (!launcherStep) return 'pending';
        return launcherStep.status === 'completed' ? 'completed' :
               launcherStep.status === 'processing' ? 'processing' :
               launcherStep.status === 'stopped' ? 'stopped' : 'pending';

      case 'creative_assets':
        // A COPY launch recreates the source campaign's assets during the API
        // step. It does not need the separate assign-creatives step used by a
        // newly-created shell campaign.
        if (isCopyWorkflow && orchestrationStatus?.status === 'completed' &&
            orchestrationStatus?.apiResult?.success !== false) {
          return 'completed';
        }

        // Special handling for YouTube DV360: if orchestration is completed and has SDF files, treat as completed
        const isYoutubeDv360Local = campaign?.destination?.toLowerCase() === 'youtube-dv360';
        if (isYoutubeDv360Local && orchestrationStatus?.status === 'completed' &&
            orchestrationStatus?.apiResult?.uploaded_sdf_files &&
            orchestrationStatus.apiResult.uploaded_sdf_files.length > 0) {
          return 'completed';
        }

        // For shell workflows, use assign-creatives status from campaign messages; for regular workflows, use creative step
        if (isShellWorkflowLocal) {
          if (!assignCreativesStatus) return 'skipped'; // Default to skipped if no assign-creatives data
          return assignCreativesStatus.status === 'completed' ? 'completed' :
                 assignCreativesStatus.status === 'processing' ? 'processing' :
                 assignCreativesStatus.status === 'failed' ? 'failed' :
                 assignCreativesStatus.status === 'timeout' ? 'timeout' :
                 assignCreativesStatus.status === 'stopped' ? 'stopped' : 'skipped';
        } else {
          const creativeStep = orchestrationStatus.stepProgress.creative;
          if (!creativeStep) return 'pending';
          if (creativeStep.status === 'skipped') return 'skipped';
          return creativeStep.status === 'completed' ? 'completed' :
                 creativeStep.status === 'processing' ? 'processing' :
                 creativeStep.status === 'failed' ? 'failed' :
                 creativeStep.status === 'timeout' ? 'timeout' :
                 creativeStep.status === 'stopped' ? 'stopped' : 'pending';
        }

      case 'uploaded_to_platform':
        // For shell workflows (creative assets skipped), ALWAYS keep upload stage as pending
        if (isShellWorkflowLocal) {
          return 'pending';
        }

        // For regular workflows only - based on overall orchestration completion
        // Check api_result.success for failed uploads
        if (orchestrationStatus.status === 'completed') {
          if (orchestrationStatus.apiResult && orchestrationStatus.apiResult.success === false) {
            return 'failed';
          }
          return 'completed';
        }

        return orchestrationStatus.status === 'processing' &&
               orchestrationStatus.stepProgress.api?.status === 'completed' ? 'processing' :
               'pending';

      default:
        return 'pending';
    }
  };

  // For single message mode, always show pending (unfilled) bars
  // The progress bar represents orchestration, not the single message
  const getStageStatusForSingleMessage = () => {
    // Always return 'pending' to show unfilled gray bars
    // Single messages don't fill the orchestration progress bar
    return 'pending';
  };

  // Get overall progress percentage from v2 orchestration format
  let progressPercentage = orchestrationStatus?.progressPercentage || 0;

  // The backend marks a completed API launch as 100%. Preserve that terminal
  // state for COPY workflows even if an older row still carries the shell
  // workflow's pre-upload percentage.
  if (isCopyWorkflow && isCompleted) {
    progressPercentage = 100;
  }

  // For single message mode, always show 0% progress
  // Single messages don't represent orchestration progress
  if (isSingleMessageMode) {
    progressPercentage = 0;
  }

  // For shell workflows, only cap at 75% when waiting for creatives (not when actively processing)
  const isCreativeProcessingActive = orchestrationStatus?.currentStep === 'assign-creatives' ||
                                    orchestrationStatus?.currentStep === 'upload-creatives' ||
                                    assignCreativesStatus?.status === 'processing';

  // For shell workflows, only cap at 75% when waiting for creatives (not when actively processing)
  if (isShellWorkflow && !creativeCompleted && !isCreativeProcessingActive && progressPercentage > 75) {
    progressPercentage = 75; // Cap at 75% only when waiting for creatives (not during active processing)
  }

  // Get completion status for the final indicator
  const getCompletionStatus = () => {
    // For single message mode, always show pending
    // The progress bar represents orchestration, not single messages
    if (isSingleMessageMode) {
      return 'pending';
    }

    // For orchestration mode
    if (isCompleted) return 'completed';
    if (orchestrationStatus?.status === 'review') return 'review';
    if (isStopped) return 'stopped';
    if (isFailed || isTimedOut) return 'error';
    if (!orchestrationStatus || progressPercentage === 0) return 'pending';
    return 'processing';
  };

  // Check if API has failed to add appropriate CSS class
  const apiHasFailedGlobal = orchestrationStatus?.stepProgress?.api?.status === 'failed' ||
                            (orchestrationStatus?.apiResult?.success === false) ||
                            isFailed || isStopped;

  return (
    <div className={`campaign-completion-status ${isShellWorkflow ? 'shell-workflow' : ''} ${apiHasFailedGlobal ? 'api-failed' : ''} ${className}`}>
      {/* Campaign Header with Status Icon */}
      <div className="completion-header">
        <div className="campaign-title">
          <h3>{campaign?.name}</h3>
        </div>
      </div>

      {/* Progress Bar with 4 Stages - Hide for import-campaign */}
      {!isImportCampaign && (
      <>
      <div className="progress-stages">
        <div className="stages-container">
          {stages.map((stage, index) => {
            // Use single message status if in single message mode, otherwise use orchestration status
            const status = isSingleMessageMode ? getStageStatusForSingleMessage(stage.key) : getStageStatus(stage.key);
            const isLast = index === stages.length - 1;
            // For fast-track-shell: no half-complete bars, first bar is always full size
            // Half-complete logic disabled for shell workflows
            const isHalfComplete = false; // First bar always full size in shell workflows
            const isUploadProcessing = false; // No special upload processing animation

            return (
              <div key={stage.key} className="stage-item">
                {/* Stage Label */}
                <div className="stage-label">{stage.label}</div>

                {/* Progress Bar Segment */}
                <div className="stage-progress">
                  <div className={`progress-segment ${status} ${isHalfComplete ? 'half-complete' : ''} ${isUploadProcessing ? 'upload-processing' : ''}`}>
                    <div className="progress-fill"></div>
                  </div>
                  {!isLast && <div className="stage-connector"></div>}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Completion Indicator */}
        <div className="completion-indicator">
          <span className={`completion-dot ${getCompletionStatus()}`}></span>
          <span className="completion-text">
            {isCompleted ? 'Complete' :
             orchestrationStatus?.status === 'review' ? 'Ready for Review' :
             isStopped ? 'Stopped' :
             isTimedOut ? 'Timed Out' :
             isFailed ? 'Failed' :
             `${Math.round(progressPercentage)}%`}
          </span>

          {/* Stop Button - only show when processing and stop handler is provided, but hide for upload/assign creatives */}
          {orchestrationStatus?.status === 'processing' &&
           onStopOrchestration &&
           orchestrationStatus?.currentStep !== 'upload-creatives' &&
           orchestrationStatus?.currentStep !== 'assign-creatives' && (
            <button
              className="stop-orchestration-btn"
              onClick={onStopOrchestration}
              title="Stop orchestration"
            >
              <FontAwesomeIcon icon={faStop} />
              Stop
            </button>
          )}

          {/* Start Orchestration Button - only show in single message mode */}
          {isSingleMessageMode && (
            <button
              className="start-orchestration-btn"
              onClick={() => setShowStartOrchestrationModal(true)}
              title="Upload files to start orchestration"
            >
              <FontAwesomeIcon icon={faRocket} />
              Start Orchestration
            </button>
          )}
        </div>
      </div>

      {/* Sub-tasks with Checkmarks - Column Layout (only show for orchestration, not single messages) */}
      {!isSingleMessageMode && (
        <div className="sub-tasks-grid">
          {subTaskColumns.map((column, columnIndex) => (
            <div key={columnIndex} className={`sub-task-column ${column.tasks.length === 0 ? 'empty' : ''}`}>
              {column.tasks.map((task) => {
                const taskStatus = getSubTaskStatus(task.key);

              // Render different icons and styles based on status
              const renderTaskIcon = () => {
                switch (taskStatus) {
                  case 'completed':
                  case 'skipped':
                  case 'timeout':
                  case 'failed':
                  // 'stopped' is intentionally grouped here: when a user halts
                  // the orchestration after the API stage already succeeded,
                  // they still need the creative_assets upload button so they
                  // can finish uploading. Non-creative-assets tasks fall
                  // through to the 'stopped' branch in the fallback below.
                  case 'stopped':
                    // Check if this is creative assets and platform supports upload or download
                    const isCreativeAssets = task.key === 'creative_assets';
                    const destination = campaign?.destination?.toLowerCase();
                    const isYoutubeDv360 = destination === 'youtube-dv360';
                    const supportsUpload = campaign?.destination &&
                      ['tiktok', 'dv360', 'ttd', 'facebook', 'reddit', 'google-ads-search', 'google-ads-pmax', 'google-ads-demandgen', 'madhive', 'openai'].includes(destination) &&
                      // Only require advertiser_id for platforms that actually need it
                      (destination === 'facebook' || destination === 'reddit' || destination === 'google-ads-search' || destination === 'google-ads-pmax' || destination === 'google-ads-demandgen' || destination === 'openai' || campaign.advertiser_id || (destination === 'tiktok' && resolveTikTokAdvertiserId(campaign, orchestrationStatus?.apiResult)));
                    const supportsDownload = isYoutubeDv360 && campaign.advertiser_id;

                    if (isCreativeAssets && (supportsUpload || supportsDownload)) {
                      const uploadInProgress = uploadCreativesData && uploadCreativesData.status === 'processing';

                      // Check if API has failed or orchestration is in a failed state.
                      // Note: a 'stopped' orchestration is NOT a failure — the user halted it,
                      // and if the API already succeeded they should still be able to re-upload.
                      const apiHasFailed = orchestrationStatus?.stepProgress?.api?.status === 'failed' ||
                                          (orchestrationStatus?.apiResult?.success === false) ||
                                          isFailed;

                      if (isYoutubeDv360 && !apiHasFailed) {
                        // YouTube DV360: Show download SDF files button and upload creatives button
                        const isCompleted = taskStatus === 'completed';
                        const hasSDFFiles = orchestrationStatus?.apiResult?.uploaded_sdf_files &&
                                          orchestrationStatus.apiResult.uploaded_sdf_files.length > 0;

                        if (isCompleted && hasSDFFiles) {
                          // Separate Line Item and Ad Group SDF files
                          const lineItemSDF = orchestrationStatus.apiResult.uploaded_sdf_files.find(f =>
                            f.type === 'line_item_sdf' || f.filename.includes('LineItems')
                          );
                          const adGroupSDF = orchestrationStatus.apiResult.uploaded_sdf_files.find(f =>
                            f.type === 'ad_groups_sdf' || f.filename.includes('AdGroups')
                          );

                          return (
                            <div>
                              {/* File Actions Dropdown */}
                              <button
                                className="sub-task-warning clickable"
                                onClick={() => setShowYoutubeActionsDropdown(!showYoutubeActionsDropdown)}
                                title="File Actions"
                                style={{
                                  width: '100%',
                                  justifyContent: 'space-between',
                                  backgroundColor: '#fff9e6',
                                  border: '2px solid #f0ad4e',
                                  color: '#8a6d3b'
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span>File Actions</span>
                                </span>
                                <span style={{ fontSize: '10px', marginLeft: '8px' }}>
                                  {showYoutubeActionsDropdown ? '▲' : '▼'}
                                </span>
                              </button>

                              {/* Dropdown Menu */}
                              {showYoutubeActionsDropdown && (
                                <div
                                  className="youtube-actions-dropdown"
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'transparent',
                                    zIndex: 1000,
                                    marginTop: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px'
                                  }}
                                >
                                  {/* Download Line Item SDF */}
                                  {lineItemSDF && (
                                    <button
                                      className="sub-task-warning clickable download-sdf"
                                      onClick={() => {
                                        handleDownloadSDFFiles([lineItemSDF]);
                                        setShowYoutubeActionsDropdown(false);
                                      }}
                                      title="Download Line Item SDF file"
                                      style={{ width: '100%' }}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                      <span>Download Line Item SDF</span>
                                    </button>
                                  )}

                                  {/* Download Ad Group SDF */}
                                  {adGroupSDF && (
                                    <button
                                      className="sub-task-warning clickable download-sdf"
                                      onClick={() => {
                                        handleDownloadSDFFiles([adGroupSDF]);
                                        setShowYoutubeActionsDropdown(false);
                                      }}
                                      title="Download Ad Group SDF file"
                                      style={{ width: '100%' }}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                      <span>Download Ad Group SDF</span>
                                    </button>
                                  )}

                                  {/* Upload Creatives Button */}
                                  {onUploadCreatives && (
                                    <button
                                      className="sub-task-warning clickable"
                                      onClick={() => {
                                        onUploadCreatives();
                                        setShowYoutubeActionsDropdown(false);
                                      }}
                                      title="Upload YouTube creative assets"
                                      style={{ width: '100%' }}
                                    >
                                      <FontAwesomeIcon icon={faUpload} />
                                      <span>Upload Creatives</span>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        } else if (isCompleted) {
                          // Show completed checkmark if no SDF files available
                          return <FontAwesomeIcon icon={faCheckCircle} className="sub-task-check completed" />;
                        } else {
                          // Show generic label for non-completed states
                          const genericLabel = isYoutubeDv360 ? 'SDF files' : 'Creative assets';
                          return (
                            <div className="sub-task-warning">
                              <FontAwesomeIcon icon={isYoutubeDv360 ? faDownload : faUpload} />
                              <span>{genericLabel}</span>
                            </div>
                          );
                        }
                      } else if (onUploadCreatives && !apiHasFailed && !isYoutubeDv360) {
                        // Regular platforms: Only show upload button if API hasn't failed and orchestration isn't stopped/failed
                        const isRetry = taskStatus === 'failed' || taskStatus === 'timeout';
                        const isCompleted = taskStatus === 'completed';

                        // Show "Upload more" button even when there's an upload in progress
                        const buttonText = uploadInProgress ? 'Upload more' :
                                         isRetry ? 'Retry upload' :
                                         isCompleted ? 'Upload more' : 'Upload creatives';
                        const buttonTitle = uploadInProgress ? 'Click to upload additional creative assets' :
                                          taskStatus === 'timeout' ? 'Click to retry uploading creative assets (previous attempt timed out)' :
                                          taskStatus === 'failed' ? 'Click to retry uploading creative assets' :
                                          isCompleted ? 'Click to upload additional creative assets' :
                                          'Click to upload creative assets';

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {uploadInProgress && (
                              <FontAwesomeIcon icon={faClock} className="sub-task-check processing" title="Upload in progress" />
                            )}
                            <button
                              className={`sub-task-warning clickable ${isRetry ? 'retry' : (isCompleted || uploadInProgress) ? 'upload-more' : ''}`}
                              onClick={onUploadCreatives}
                              title={buttonTitle}
                            >
                              <FontAwesomeIcon icon={faUpload} />
                              <span>{buttonText}</span>
                            </button>
                          </div>
                        );
                      } else if (apiHasFailed) {
                        // When API has failed, don't show anything in the creative assets area
                        return null;
                      }
                    }

                    // Fallback for non-creative-assets tasks or unsupported platforms
                    if (taskStatus === 'completed') {
                      return <FontAwesomeIcon icon={faCheckCircle} className="sub-task-check completed" />;
                    } else if (taskStatus === 'failed') {
                      return <FontAwesomeIcon icon={faExclamationTriangle} className="sub-task-check error" />;
                    } else if (taskStatus === 'skipped') {
                      // Show generic label only for skipped state without upload capability
                      const skippedLabel = isYoutubeDv360 ? 'SDF files' : 'Creative assets';
                      return (
                        <div className="sub-task-warning">
                          <FontAwesomeIcon icon={isYoutubeDv360 ? faDownload : faUpload} />
                          <span>{skippedLabel}</span>
                        </div>
                      );
                    } else if (taskStatus === 'timeout') {
                      return <FontAwesomeIcon icon={faExclamationTriangle} className="sub-task-check error" />;
                    } else if (taskStatus === 'stopped') {
                      return <div className="sub-task-check stopped empty-circle"></div>;
                    }
                    // Default fallback for this case group
                    return <div className="sub-task-check pending empty-circle"></div>;
                  case 'processing':
                    return <FontAwesomeIcon icon={faClock} className="sub-task-check processing" />;
                  default:
                    return <div className="sub-task-check pending empty-circle"></div>;
                }
              };

              // For shell workflows, handle label display logic
              const isCreativeAssetsCompleted = task.key === 'creative_assets' &&
                                               isShellWorkflow &&
                                               taskStatus === 'completed';
              const isUploadedToPlatformInShell = task.key === 'uploaded_to_platform' &&
                                                 isShellWorkflow &&
                                                 creativeCompleted;

              // Show "Uploaded to Platform" for completed creative assets, hide duplicate for uploaded_to_platform
              let displayLabel = task.label;
              if (isCreativeAssetsCompleted) {
                displayLabel = `Uploaded to ${campaign?.destination?.toUpperCase() || 'Platform'}`;
              } else if (isUploadedToPlatformInShell) {
                displayLabel = ''; // Hide duplicate label
              }

              // Check if any upload/download button is being shown (for any creative assets status).
              // Includes 'stopped' so the redundant "Creative assets" label is hidden when the
              // upload button has taken over the row — see renderTaskIcon above.
              const destination = campaign?.destination?.toLowerCase();
              const apiHasFailed = orchestrationStatus?.stepProgress?.api?.status === 'failed' ||
                                  (orchestrationStatus?.apiResult?.success === false) ||
                                  isFailed;
              const isYoutubeDv360 = destination === 'youtube-dv360';
              const isUploadButtonShown = task.key === 'creative_assets' &&
                                        (taskStatus === 'failed' || taskStatus === 'timeout' || taskStatus === 'completed' || taskStatus === 'skipped' || taskStatus === 'stopped') &&
                                        !apiHasFailed && // Don't show button if API has failed
                                        campaign?.destination &&
                                        (
                                          // Regular upload platforms (use frontend format: google-ads-search, not google_search)
                                          (onUploadCreatives && ['tiktok', 'dv360', 'ttd', 'facebook', 'reddit', 'google-ads-search', 'google-ads-pmax', 'google-ads-demandgen', 'madhive', 'openai'].includes(destination) &&
                                           // Only require advertiser_id for platforms that actually need it
                                           (destination === 'facebook' || destination === 'reddit' || destination === 'google-ads-search' || destination === 'google-ads-pmax' || destination === 'google-ads-demandgen' || destination === 'openai' || campaign.advertiser_id || (destination === 'tiktok' && resolveTikTokAdvertiserId(campaign, orchestrationStatus?.apiResult)))) ||
                                          // YouTube DV360 download platform
                                          (isYoutubeDv360 && campaign.advertiser_id)
                                        );

              // For creative assets and uploaded_to_platform, don't render anything if API has failed.
              // 'stopped' is excluded so the task row (and its upload button) stays visible after a stop.
              const apiHasFailedForTask = orchestrationStatus?.stepProgress?.api?.status === 'failed' ||
                                         (orchestrationStatus?.apiResult?.success === false) ||
                                         isFailed;

              if ((task.key === 'creative_assets' || task.key === 'uploaded_to_platform') && apiHasFailedForTask) {
                return null; // Don't render upload-related tasks when API has failed
              }

              return (
                <div key={task.key} className="sub-task-item">
                  {taskStatus === 'skipped' ? (
                    renderTaskIcon()
                  ) : (
                    <>
                      {renderTaskIcon()}
                      {/* Don't show label when any upload button is displayed */}
                      {displayLabel && !isUploadButtonShown && <span className="sub-task-label">{displayLabel}</span>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        </div>
      )}
      </>
      )}
      {/* End of orchestration section - hidden for import-campaign */}

      {/* Start Orchestration Modal */}
      <StartOrchestrationModal
        isOpen={showStartOrchestrationModal}
        onClose={() => setShowStartOrchestrationModal(false)}
        campaign={campaign}
        accountId={accountId}
        user={{ userId }}
        onOrchestrationStart={(data) => {
          console.log('📨 CampaignCompletionStatus received orchestration data:', data);
          // Notify parent component to start polling
          if (onOrchestrationStart) {
            console.log('📤 Forwarding to parent (CampaignDetailPage)');
            onOrchestrationStart(data);
          } else {
            console.error('❌ onOrchestrationStart prop not provided to CampaignCompletionStatus!');
          }
        }}
      />

      {/* Campaign Actions Section - Rendered when platform URLs are available */}
      {campaign && platformUrls && platformUrls.length > 0 && (
        <div className="campaign-actions-wrapper" style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid #e9ecef'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', fontWeight: '600', color: '#333', textAlign: 'center' }}>Campaign Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Render platform view buttons */}
            {platformUrls.map((urlData, index) => {
              if (urlData.multiple && Array.isArray(urlData.campaigns) && campaign?.destination?.toLowerCase() === 'ttd') {
                // TTD: Compact dropdown for multiple campaigns + action buttons
                const hasViewUrls = urlData.campaigns.some(c => c.url);
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}
                  >
                    {/* View dropdown — only shown when at least one campaign has a URL */}
                    {hasViewUrls && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <select
                          className="form-control"
                          style={{
                            width: 'auto',
                            maxWidth: '300px',
                            padding: '10px 12px',
                            fontSize: '14px',
                            fontWeight: '500',
                            border: '1px solid #007bff',
                            color: '#007bff',
                            borderRadius: '4px',
                            backgroundColor: 'transparent',
                            cursor: 'pointer'
                          }}
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              window.open(e.target.value, '_blank');
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="" disabled>View in TTD ({urlData.campaigns.length} campaigns)</option>
                          {urlData.campaigns.filter(c => c.url).map((c, i) => (
                            <option key={c.campaign_id} value={c.url}>
                              {c.name || `Campaign ${i + 1}`} ({c.campaign_id})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Edit Campaign — campaign selector is in the modal (hidden for imported youtube-dv360) */}
                    {onEditCampaign && !(urlData.isImported && campaign?.destination === 'youtube-dv360') && (
                      <button
                        onClick={() => onEditCampaign(urlData.campaigns[0].campaign_id)}
                        className="btn btn-secondary"
                        style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Edit Campaign
                      </button>
                    )}

                    {/* Create Geofence Ad Groups — TTD only, gated by the geofence-adgroups account flag */}
                    {onCreateGeofenceAdGroups && (
                      <button
                        onClick={() => onCreateGeofenceAdGroups(urlData.campaigns[0].campaign_id)}
                        disabled={isCreatingGeofence}
                        className="btn btn-outline"
                        style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', border: '1px solid #6f42c1', color: '#6f42c1', borderRadius: '4px', backgroundColor: 'transparent', cursor: isCreatingGeofence ? 'not-allowed' : 'pointer' }}
                        onMouseEnter={(e) => { if (!isCreatingGeofence) { e.currentTarget.style.backgroundColor = '#6f42c1'; e.currentTarget.style.color = 'white'; } }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6f42c1'; }}
                      >
                        {isCreatingGeofence ? <><FontAwesomeIcon icon={faSpinner} spin /> Starting…</> : <><FontAwesomeIcon icon={faLocationDot} /> Create Geofence Ad Groups</>}
                      </button>
                    )}

                    {/* Optimize Campaign (hidden for imported youtube-dv360; on DV360 the
                        Run Full Analysis button replaces the combined optimizer) */}
                    {onOptimizeCampaign && urlData.isImported && urlData.supportsOptimize && campaign?.destination !== 'youtube-dv360' && !optimizeAnalysesEnabledFor(campaign?.destination) && (
                      <button
                        onClick={() => onOptimizeCampaign(urlData.campaigns[0].campaign_id)}
                        disabled={isOptimizing}
                        className="btn btn-primary"
                        style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: isOptimizing ? 'not-allowed' : 'pointer' }}
                        onMouseEnter={(e) => { if (!isOptimizing) e.currentTarget.style.backgroundColor = '#0056b3'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#007bff'; }}
                      >
                        {isOptimizing ? <><FontAwesomeIcon icon={faSpinner} spin /> Optimizing...</> : <><FontAwesomeIcon icon={faChartLine} /> Optimize Campaign</>}
                      </button>
                    )}

                    {/* Launched TTD campaigns use this compact layout too, even
                        when the plan produced only one platform campaign. */}
                    {onOptimizeCampaign && urlData.supportsOptimize && urlData.campaigns[0]?.campaign_id &&
                     (urlData.isImported ||
                      (orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status))) && (
                      <FocusedAnalysisButtons
                        destination={campaign?.destination}
                        platformCampaignId={urlData.campaigns[0].campaign_id}
                        onOptimizeCampaign={onOptimizeCampaign}
                        isOptimizing={isOptimizing}
                        runningKind={optimizingAnalysisKind}
                      />
                    )}

                    <RunAuditButton
                      destination={campaign?.destination}
                      platformCampaignId={urlData.campaigns[0]?.campaign_id}
                      onRunAudit={onRunAudit}
                      isAuditing={isAuditing}
                      launched={urlData.isImported
                      || (orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status))}
                    />

                    {/* Refresh Campaign */}
                    {onRefreshCampaign && urlData.isImported && urlData.campaigns[0]?.campaign_id && (
                      <button
                        onClick={() => onRefreshCampaign(urlData.campaigns[0].campaign_id)}
                        disabled={isRefreshing}
                        className="btn btn-outline"
                        style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', border: '1px solid #007bff', color: '#007bff', borderRadius: '4px', backgroundColor: 'transparent', cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
                        onMouseEnter={(e) => { if (!isRefreshing) { e.currentTarget.style.backgroundColor = '#007bff'; e.currentTarget.style.color = 'white'; } }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#007bff'; }}
                      >
                        {isRefreshing ? <><FontAwesomeIcon icon={faSpinner} spin /> Refreshing...</> : <><FontAwesomeIcon icon={faRefresh} /> Refresh Campaign</>}
                      </button>
                    )}

                    {/* Upload Assets */}
                    {onUploadCreatives && urlData.isImported && (
                      <button
                        onClick={onUploadCreatives}
                        className="btn btn-success"
                        style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                      >
                        <FontAwesomeIcon icon={faUpload} />
                        Upload Assets
                      </button>
                    )}
                  </div>
                );
              } else if (urlData.multiple && Array.isArray(urlData.campaigns)) {
                // Multiple campaigns with URLs and IDs (Google Ads Search format)
                return urlData.campaigns.map((campaignData, urlIndex) => (
                  <div
                    key={`${index}-${urlIndex}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <a
                      href={campaignData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: '1px solid #007bff',
                        color: '#007bff',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#007bff';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#007bff';
                      }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      {/* Name the campaign when the platform gave us one — a
                          Market Expansion launch produces several campaigns that
                          differ only by market, and identical labels make the
                          buttons impossible to tell apart. */}
                      {campaignData.name ? `${urlData.label} — ${campaignData.name}` : urlData.label}
                    </a>

                    {/* Refresh button for this specific campaign (multi-campaign) - placed next to View */}
                    {onRefreshCampaign &&
                     orchestrationStatus?.status === 'completed' &&
                     campaign.status === 'ready_to_launch' && (
                      <button
                        onClick={() => onRefreshCampaign(campaignData.campaign_id)}
                        disabled={isRefreshing}
                        className="btn btn-outline"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #007bff',
                          color: '#007bff',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!isRefreshing) {
                            e.currentTarget.style.backgroundColor = '#007bff';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#007bff';
                        }}
                      >
                        {isRefreshing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faRefresh} />
                            Refresh Campaign
                          </>
                        )}
                      </button>
                    )}

                    {/* Edit button for this specific campaign */}
                    {/* MadHive: enable edit once API step completes (don't wait for assign-creatives) */}
                    {onEditCampaign && (
                      campaign?.destination?.toLowerCase() !== 'madhive' ||
                      orchestrationStatus?.stepProgress?.api?.status === 'completed'
                    ) && (
                      <button
                        onClick={() => onEditCampaign(campaignData.campaign_id)}
                        className="btn btn-secondary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Edit Campaign
                      </button>
                    )}

                    {/* Optimize button for this specific campaign (on DV360 the Run Full
                        Analysis button replaces the combined optimizer) */}
                    {urlData.supportsOptimize && onOptimizeCampaign &&
                     !optimizeAnalysesEnabledFor(campaign?.destination) &&
                     orchestrationStatus?.status === 'completed' &&
                     hasLaunched(campaign.status) && (
                      <button
                        onClick={() => onOptimizeCampaign(campaignData.campaign_id)}
                        disabled={isOptimizing}
                        className="btn btn-primary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {isOptimizing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Optimizing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faChartLine} />
                            Optimize Campaign
                          </>
                        )}
                      </button>
                    )}

                    {urlData.supportsOptimize && onOptimizeCampaign &&
                     orchestrationStatus?.status === 'completed' &&
                     hasLaunched(campaign.status) && (
                      <FocusedAnalysisButtons
                        destination={campaign?.destination}
                        platformCampaignId={campaignData.campaign_id}
                        onOptimizeCampaign={onOptimizeCampaign}
                        isOptimizing={isOptimizing}
                        runningKind={optimizingAnalysisKind}
                      />
                    )}

                    <RunAuditButton
                      destination={campaign?.destination}
                      platformCampaignId={campaignData.campaign_id}
                      onRunAudit={onRunAudit}
                      isAuditing={isAuditing}
                      launched={orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status)}
                    />
                  </div>
                ));
              } else if (urlData.multiple && Array.isArray(urlData.urls)) {
                // Multiple URLs for same platform (legacy format)
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}
                  >
                    {urlData.urls.map((url, urlIndex) => (
                      <a
                        key={`${index}-${urlIndex}`}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#868e96',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#868e96'}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        {urlData.label} {urlIndex + 1}
                      </a>
                    ))}
                    {/* Edit Campaign Button for multiple-URL campaigns */}
                    {onEditCampaign && (
                      <button
                        onClick={() => onEditCampaign(campaign.platform_campaign_id)}
                        className="btn btn-secondary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Edit Campaign
                      </button>
                    )}
                  </div>
                );
              } else {
                // Single URL
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}
                  >
                    {urlData.url && (
                      <a
                        href={urlData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #007bff',
                          color: '#007bff',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#007bff';
                        }}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        {urlData.label}
                      </a>
                    )}

                    {/* Refresh Campaign Button for single campaign - placed next to View Campaign */}
                    {onRefreshCampaign && !urlData.multiple && urlData.campaign_id && (
                      (urlData.isImported ||
                       (orchestrationStatus?.status === 'completed' && campaign.status === 'ready_to_launch')) && (
                      <button
                        onClick={() => onRefreshCampaign(urlData.campaign_id)}
                        disabled={isRefreshing}
                        className="btn btn-outline"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #007bff',
                          color: '#007bff',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!isRefreshing) {
                            e.currentTarget.style.backgroundColor = '#007bff';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#007bff';
                        }}
                      >
                        {isRefreshing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Refreshing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faRefresh} />
                            Refresh Campaign
                          </>
                        )}
                      </button>
                    ))}

                    {/* Edit Campaign Button for single campaign (hidden for imported youtube-dv360) */}
                    {onEditCampaign && !urlData.multiple && !(urlData.isImported && campaign?.destination === 'youtube-dv360') && (
                      <button
                        onClick={() => onEditCampaign(urlData.campaign_id)}
                        className="btn btn-secondary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Edit Campaign
                      </button>
                    )}

                    {/* Create Geofence Ad Groups — TTD only, gated by the geofence-adgroups account flag.
                        Mirrors the multiple-campaigns block so single/imported TTD campaigns get it too. */}
                    {onCreateGeofenceAdGroups && !urlData.multiple && urlData.campaign_id && (
                      <button
                        onClick={() => onCreateGeofenceAdGroups(urlData.campaign_id)}
                        disabled={isCreatingGeofence}
                        className="btn btn-outline"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #6f42c1',
                          color: '#6f42c1',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          cursor: isCreatingGeofence ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => { if (!isCreatingGeofence) { e.currentTarget.style.backgroundColor = '#6f42c1'; e.currentTarget.style.color = 'white'; } }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6f42c1'; }}
                      >
                        {isCreatingGeofence ? <><FontAwesomeIcon icon={faSpinner} spin /> Starting…</> : <><FontAwesomeIcon icon={faLocationDot} /> Create Geofence Ad Groups</>}
                      </button>
                    )}

                    {/* Optimize Campaign Button for single campaign (hidden for imported
                        youtube-dv360; on DV360 the Run Full Analysis button replaces the
                        combined optimizer) */}
                    {onOptimizeCampaign && !urlData.multiple && urlData.supportsOptimize && campaign?.destination !== 'youtube-dv360' && !optimizeAnalysesEnabledFor(campaign?.destination) && (
                      // For imported campaigns, show optimize button without orchestration check
                      // For regular campaigns, check orchestration status
                      (urlData.isImported ||
                       (orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status))) && (
                      <button
                        onClick={() => onOptimizeCampaign(urlData.campaign_id)}
                        disabled={isOptimizing}
                        className="btn btn-primary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        {isOptimizing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Optimizing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faChartLine} />
                            Optimize Campaign
                          </>
                        )}
                      </button>
                    ))}

                    {onOptimizeCampaign && !urlData.multiple && urlData.supportsOptimize && campaign?.destination !== 'youtube-dv360' && (
                      (urlData.isImported ||
                       (orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status))) && (
                      <FocusedAnalysisButtons
                        destination={campaign?.destination}
                        platformCampaignId={urlData.campaign_id}
                        onOptimizeCampaign={onOptimizeCampaign}
                        isOptimizing={isOptimizing}
                        runningKind={optimizingAnalysisKind}
                      />
                    ))}

                    <RunAuditButton
                      destination={campaign?.destination}
                      platformCampaignId={urlData.campaign_id}
                      onRunAudit={onRunAudit}
                      isAuditing={isAuditing}
                      launched={urlData.isImported
                      || (orchestrationStatus?.status === 'completed' && hasLaunched(campaign.status))}
                    />

                    {/* Upload Assets Button for imported campaigns */}
                    {onUploadCreatives && urlData.isImported && (
                      <button
                        onClick={onUploadCreatives}
                        className="btn btn-success"
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                      >
                        <FontAwesomeIcon icon={faUpload} />
                        Upload Assets
                      </button>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignCompletionStatus;
